import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowClockwise, MagnifyingGlass, PaperPlaneTilt, PencilSimple, ChatCircleDots, CaretDown, CaretRight, Info } from '@phosphor-icons/react';

function NotificationsPage() {
  const [tab, setTab] = useState('log');
  return (
    <Layout>
      <div className="space-y-6 pb-12">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-zinc-950">WhatsApp Notifications</h1>
          <p className="text-sm text-zinc-500 mt-1">Send manual messages, view the queue, and map Meta-approved templates.</p>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-zinc-200">
          {[
            { k: 'log', label: 'Log' },
            { k: 'templates', label: 'Templates' },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`notif-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
                tab === t.k ? 'border-orange-500 text-orange-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >{t.label}</button>
          ))}
        </div>

        {tab === 'log' ? <LogTab /> : <TemplatesTab />}
      </div>
    </Layout>
  );
}

function LogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', event_key: '', customer_mobile: '' });
  const [showSend, setShowSend] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const { data } = await api.get('/api/admin/notifications/queue', { params });
      setRows(data.notifications || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    try {
      await api.post(`/api/admin/notifications/queue/${id}/retry`);
      toast.success('Retried');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    }
  };

  const checkDelivery = async (id) => {
    try {
      const { data } = await api.post(`/api/admin/notifications/queue/${id}/check-delivery`);
      const st = data.delivery_status || 'unknown';
      if (st === 'delivered' || st === 'read') toast.success(`WhatsApp status: ${st.toUpperCase()}`);
      else toast.warning(`WhatsApp status: ${st.toUpperCase()}${data.delivery_error_code ? ` (error ${data.delivery_error_code})` : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delivery check failed');
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-3 justify-between items-end">
        <Card className="border-zinc-300 rounded-sm flex-1">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              data-testid="filter-status"
              className="h-10 px-3 border border-zinc-300 rounded-sm bg-white text-sm"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
            <Input data-testid="filter-event" placeholder="Event key (e.g. payment.link)" value={filters.event_key} onChange={(e) => setFilters({ ...filters, event_key: e.target.value })} />
            <Input data-testid="filter-mobile" placeholder="Customer Mobile" value={filters.customer_mobile} onChange={(e) => setFilters({ ...filters, customer_mobile: e.target.value })} />
            <Button data-testid="apply-filter-btn" onClick={load} className="bg-orange-500 hover:bg-orange-600 text-white">
              <MagnifyingGlass size={16} className="mr-1" /> Apply
            </Button>
          </CardContent>
        </Card>
        <div className="flex gap-2">
          <Button data-testid="refresh-btn" variant="outline" onClick={load}><ArrowClockwise size={16} className="mr-1" /> Refresh</Button>
          <Button data-testid="send-notif-btn" onClick={() => setShowSend(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
            <PaperPlaneTilt size={16} className="mr-1" /> Send
          </Button>
        </div>
      </div>

      <Card className="border-zinc-300 rounded-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-300">
              <tr>
                <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">When</th>
                <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Event</th>
                <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">To</th>
                <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Message</th>
                <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Source</th>
                <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center text-zinc-400">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No notifications</td></tr>
              ) : rows.map((r) => (
                <tr key={r._id} className="border-b border-zinc-200" data-testid={`notif-row-${r._id}`}>
                  <td className="p-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                  <td className="p-3 font-mono text-xs">{r.event_key}</td>
                  <td className="p-3 font-mono text-xs">{r.to}</td>
                  <td className="p-3 text-xs max-w-sm">
                    <div className="truncate" title={r.body}>{r.body}</div>
                    {r.content_sid && (
                      <div className="text-[10px] text-blue-700 mt-1 font-mono truncate" title={`ContentSid: ${r.content_sid}`}>
                        SID: {r.content_sid.slice(0, 12)}…
                      </div>
                    )}
                    {r.content_variables && (
                      <div className="text-[10px] text-purple-700 mt-0.5 font-mono truncate" title={`ContentVariables sent to Twilio: ${r.content_variables}`}>
                        Vars: {r.content_variables.slice(0, 60)}{r.content_variables.length > 60 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <Badge variant="outline" className={`text-[10px] uppercase ${
                      r.status === 'sent' ? 'text-emerald-700 border-emerald-300' :
                      r.status === 'failed' ? 'text-red-700 border-red-300' :
                      'text-amber-700 border-amber-300'
                    }`}>{r.status}</Badge>
                    {r.delivery_status && (
                      <div className={`text-[10px] mt-1 font-bold uppercase ${
                        r.delivery_status === 'delivered' || r.delivery_status === 'read' ? 'text-emerald-600' :
                        r.delivery_status === 'failed' || r.delivery_status === 'undelivered' ? 'text-red-600' : 'text-amber-600'
                      }`} data-testid={`delivery-status-${r._id}`}>
                        WA: {r.delivery_status}{r.delivery_error_code ? ` (${r.delivery_error_code})` : ''}
                      </div>
                    )}
                    {r.delivery_hint && (
                      <div className="text-[10px] text-zinc-500 mt-0.5 max-w-[180px] whitespace-normal" title={r.delivery_hint}>{r.delivery_hint}</div>
                    )}
                    {r.last_error && (
                      <div className="text-[10px] text-red-600 mt-1 max-w-[160px] truncate" title={r.last_error}>{r.last_error}</div>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <Badge variant="outline" className="text-[10px] uppercase">{r.source}</Badge>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {r.source === 'web' && r.status === 'sent' && (
                        <Button data-testid={`check-delivery-${r._id}`} variant="ghost" size="sm" onClick={() => checkDelivery(r._id)} title="Check WhatsApp delivery status">
                          <MagnifyingGlass size={16} />
                        </Button>
                      )}
                      {r.source === 'web' && r.status !== 'sent' && (
                        <Button data-testid={`retry-${r._id}`} variant="ghost" size="sm" onClick={() => retry(r._id)} title="Retry">
                          <ArrowClockwise size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <SendDialog open={showSend} onOpenChange={setShowSend} onSent={load} />
    </>
  );
}

function SendDialog({ open, onOpenChange, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    event_key: 'customer.welcome',
    to: '',
    body_override: '',
    send_now: true,
    ref_id: '',
    ref_type: '',
  });

  useEffect(() => {
    if (open) {
      api.get('/api/admin/notifications/templates').then(r => setTemplates(r.data || [])).catch(() => {});
    }
  }, [open]);

  const currentBody = templates.find(t => t.event_key === form.event_key)?.body || '';

  const send = async () => {
    if (!form.to) { toast.error('To is required'); return; }
    try {
      await api.post('/api/admin/notifications/send', {
        ...form,
        body_override: form.body_override || undefined,
      });
      toast.success(form.send_now ? 'Sent' : 'Queued');
      onSent && onSent();
      onOpenChange(false);
      setForm({ event_key: 'customer.welcome', to: '', body_override: '', send_now: true, ref_id: '', ref_type: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Send WhatsApp Notification</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Event</Label>
            <select
              data-testid="send-event"
              className="w-full h-10 px-3 border border-zinc-300 rounded-sm bg-white"
              value={form.event_key}
              onChange={(e) => setForm({ ...form, event_key: e.target.value })}
            >
              {templates.map((t) => <option key={t.event_key} value={t.event_key}>{t.event_key}</option>)}
            </select>
          </div>
          <div>
            <Label>To (10-digit mobile)</Label>
            <Input data-testid="send-to" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
          </div>
          <div>
            <Label className="flex justify-between">
              Body
              <span className="text-[10px] text-zinc-500">Override template body (optional). Variables in template won&apos;t be substituted in overrides.</span>
            </Label>
            <textarea
              data-testid="send-body"
              className="w-full min-h-[80px] p-2 border border-zinc-300 rounded-sm text-sm"
              value={form.body_override || ''}
              onChange={(e) => setForm({ ...form, body_override: e.target.value })}
              placeholder={currentBody}
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              data-testid="send-now"
              type="checkbox"
              checked={form.send_now}
              onChange={(e) => setForm({ ...form, send_now: e.target.checked })}
            />
            Send immediately (otherwise queued as pending)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="send-submit" onClick={send} className="bg-orange-500 hover:bg-orange-600 text-white">
            <PaperPlaneTilt size={16} className="mr-1" /> Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [bodyText, setBodyText] = useState('');
  const [showFallback, setShowFallback] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/notifications/templates');
      setTemplates(data || []);
    } catch (e) {
      toast.error('Failed to load templates');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (t) => {
    setEditing(t.event_key);
    setBodyText(t.body || '');
  };

  const save = async () => {
    try {
      await api.put('/api/admin/notifications/templates', { event_key: editing, body: bodyText });
      toast.success('Template saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Explainer banner */}
      <div className="flex gap-3 p-3 rounded-sm bg-blue-50 border border-blue-200" data-testid="templates-tab-explainer">
        <Info size={20} className="text-blue-700 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-900 leading-relaxed">
          <div className="font-bold text-sm mb-1">How WhatsApp sending actually works here</div>
          WhatsApp Business API only accepts <span className="font-bold">Meta-approved templates</span> for business-initiated messages.
          Set them up in the <span className="font-bold">&ldquo;Meta-Approved WhatsApp Templates (ContentSid)&rdquo;</span> section below — that&apos;s the only thing required for real customer notifications.
          <br />
          The old <span className="font-bold">&ldquo;Fallback body text&rdquo;</span> section (further down) is <span className="font-bold">optional</span>. It&apos;s a leftover from the freeform-message era and is used only as an internal preview label + as a fallback inside the 24-hour reply window.
          You don&apos;t need to author or edit it.
        </div>
      </div>

      {/* Primary: ContentSid mapping — this is what actually sends */}
      <ContentTemplateMapSection eventKeys={templates.map(t => t.event_key)} />

      {/* Legacy: freeform body — collapsed by default, labelled optional */}
      <Card className="border-zinc-300 rounded-sm">
        <CardHeader className="border-b border-zinc-300 cursor-pointer select-none"
                    onClick={() => setShowFallback((v) => !v)}
                    data-testid="fallback-body-toggle">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-zinc-700">
              {showFallback ? <CaretDown size={16} /> : <CaretRight size={16} />}
              <ChatCircleDots size={16} />
              Fallback / Preview Body Text
              <Badge variant="outline" className="text-[10px] ml-2 text-zinc-500">OPTIONAL — LEGACY</Badge>
            </CardTitle>
            <span className="text-[10px] text-zinc-500">{showFallback ? 'Hide' : 'Show'}</span>
          </div>
        </CardHeader>
        {showFallback && (
          <CardContent className="p-0">
            <div className="p-3 text-xs text-zinc-600 bg-amber-50 border-b border-amber-200">
              <span className="font-bold text-amber-900">Note:</span> These freeform bodies are <span className="font-bold">not sent</span> when a Meta-approved ContentSid is mapped above (which is the required setup).
              They are only used as (a) the preview text shown in the WhatsApp Log column, and (b) a fallback inside the 24-hour customer-reply window.
              You are not required to fill or edit anything here.
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-300">
                <tr>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Event Key</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Body (Preview / Fallback)</th>
                  <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                  <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700"></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.event_key} className="border-b border-zinc-200 align-top">
                    <td className="p-3 font-mono text-xs">{t.event_key}</td>
                    <td className="p-3">
                      {editing === t.event_key ? (
                        <textarea
                          data-testid={`edit-body-${t.event_key}`}
                          className="w-full min-h-[80px] p-2 border border-zinc-300 rounded-sm text-sm"
                          value={bodyText}
                          onChange={(e) => setBodyText(e.target.value)}
                        />
                      ) : (
                        <div className="text-xs whitespace-pre-wrap text-zinc-600">{t.body}</div>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {t.is_default ? <Badge variant="outline" className="text-[10px]">DEFAULT</Badge> : <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300">CUSTOM</Badge>}
                    </td>
                    <td className="p-3 text-center">
                      {editing === t.event_key ? (
                        <div className="flex gap-1 justify-center">
                          <Button data-testid={`tpl-save-${t.event_key}`} aria-label={`Save template ${t.event_key}`} size="sm" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
                          <Button data-testid={`tpl-cancel-${t.event_key}`} variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <Button data-testid={`tpl-edit-${t.event_key}`} variant="ghost" size="sm" onClick={() => startEdit(t)}><PencilSimple size={16} /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function ContentTemplateMapSection({ eventKeys }) {
  const [mappings, setMappings] = useState({});
  const [twilioTemplates, setTwilioTemplates] = useState(null);
  const [loadingTwilio, setLoadingTwilio] = useState(false);
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/notifications/content-map');
      const map = {};
      (data.mappings || []).forEach(m => { map[m.event_key] = { content_sid: m.content_sid || '', variables: (m.variables || []).join(', ') }; });
      setMappings(map);
    } catch (e) { /* silent */ }
    try {
      const { data } = await api.get('/api/admin/notifications/content-map/health');
      setHealth(data);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (ek, field, value) => {
    setMappings(prev => ({ ...prev, [ek]: { content_sid: '', variables: '', ...prev[ek], [field]: value } }));
  };

  const save = async (ek) => {
    const m = mappings[ek] || { content_sid: '', variables: '' };
    try {
      const { data } = await api.put('/api/admin/notifications/content-map', {
        event_key: ek,
        content_sid: m.content_sid || '',
        variables: (m.variables || '').split(',').map(s => s.trim()).filter(Boolean),
      });
      if (data.warning) {
        toast.warning(data.warning, { duration: 15000 });
      } else {
        toast.success(m.content_sid ? `Approved template linked to ${ek}${data.template_variables ? ' — variables match ✓' : ''}` : `Mapping cleared for ${ek}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const autofill = async (ek) => {
    const m = mappings[ek] || {};
    try {
      const { data } = await api.post('/api/admin/notifications/content-map/autofill', {
        event_key: ek, content_sid: m.content_sid || '',
      });
      if (data.unresolved?.length) {
        toast.warning(`Auto-filled, but couldn't guess: ${data.unresolved.join(', ')} — please review those.`, { duration: 12000 });
      } else {
        toast.success(`Mapping auto-filled from Twilio for ${ek} ✓`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-fill failed');
    }
  };

  const loadTwilio = async () => {
    setLoadingTwilio(true);
    try {
      const { data } = await api.get('/api/admin/notifications/twilio-content-templates');
      setTwilioTemplates(data.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load templates from Twilio');
    } finally { setLoadingTwilio(false); }
  };

  const testSend = async (ek) => {
    const to = window.prompt(`Send a test WhatsApp for '${ek}' to which mobile number?\n(10-digit — this will send a REAL message with dummy data)`);
    if (!to) return;
    try {
      const { data } = await api.post('/api/admin/notifications/content-map/test-send', {
        event_key: ek, to: to.trim(),
      });
      const q = data.queue_row || {};
      const varsSent = q.content_variables || '(none)';
      const status = q.status || 'unknown';
      const err = q.last_error || '';
      const msg = `Test dispatched — status: ${status.toUpperCase()}` +
                  `\nContentSid: ${data.content_sid}` +
                  `\nContentVariables: ${varsSent}` +
                  (err ? `\nError: ${err}` : '');
      if (status === 'sent' || status === 'delivered') {
        toast.success(msg, { duration: 15000 });
      } else {
        toast.error(msg, { duration: 25000 });
      }
      // Also log to console for easy copy
      console.log('[TestSend]', ek, data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    }
  };

  return (
    <Card className="border-zinc-300 rounded-sm" data-testid="content-map-section">
      <CardHeader className="border-b border-zinc-300 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            Meta-Approved WhatsApp Templates (ContentSid)
            <Badge className="text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">PRIMARY</Badge>
          </CardTitle>
          <p className="text-[11px] text-zinc-500 mt-1">This is what actually sends the WhatsApp message. Map each event to your Meta-approved template SID + variables.</p>
        </div>
        <Button data-testid="load-twilio-templates-btn" variant="outline" size="sm" onClick={loadTwilio} disabled={loadingTwilio}>
          <ArrowClockwise size={14} className="mr-1" /> {loadingTwilio ? 'Loading…' : 'Load templates from Twilio'}
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {health && health.unmapped_count > 0 && (
          <div className="p-3 rounded-sm bg-red-50 border-2 border-red-300" data-testid="unmapped-events-warning">
            <div className="flex items-start gap-2">
              <div className="text-red-800 font-bold text-sm flex-shrink-0">⚠ ACTION REQUIRED</div>
            </div>
            <div className="text-xs text-red-900 mt-1 leading-relaxed">
              <span className="font-bold">{health.unmapped_count} event{health.unmapped_count === 1 ? '' : 's'}</span> below have <span className="font-bold">no Meta-approved template linked</span>.
              Messages for these events will fail (WhatsApp Business API rejects freeform outside the 24-hour reply window):
              <div className="mt-2 flex flex-wrap gap-1.5">
                {health.unmapped.map((ek) => (
                  <button key={ek} onClick={() => {
                    const el = document.querySelector(`[data-row-key="${ek}"]`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const input = document.querySelector(`[data-testid="csid-${ek}"]`);
                    if (input) input.focus();
                  }}
                  data-testid={`unmapped-${ek}`}
                  className="font-mono text-[11px] bg-red-100 hover:bg-red-200 text-red-900 px-2 py-0.5 rounded-sm border border-red-300 transition-colors">
                    {ek}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-red-700 mt-2">
                Click any event above to jump to its row and paste the ContentSid (starts with HX…). Then click Save.
              </div>
            </div>
          </div>
        )}
        {health && health.unmapped_count === 0 && (
          <div className="p-2 rounded-sm bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="all-events-mapped">
            ✓ All business-initiated events have Meta-approved templates linked.
          </div>
        )}
        <p className="text-xs text-zinc-500">
          WhatsApp requires a Meta-approved template for business-initiated messages. Link an approved template SID (starts with HX…) to an event —
          messages for that event will then be sent via the template instead of freeform text.
          Variables = comma-separated, in template order. For numbered placeholders ({'{{1}}'}) just list context keys (e.g. <span className="font-mono">customer_name</span>).
          For NAMED placeholders (e.g. {'{{Customer_name}}'}) use <span className="font-mono">TemplateVar=context_key</span> pairs, e.g.{' '}
          <span className="font-mono">Customer_name=customer_name, customer_mobile_number=mobile</span>.
        </p>

        {twilioTemplates && (
          <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 space-y-2" data-testid="twilio-templates-list">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-600">Your Twilio Templates</div>
            {twilioTemplates.length === 0 && <div className="text-xs text-zinc-400">No content templates found in this Twilio account.</div>}
            {twilioTemplates.map(t => (
              <div key={t.sid} className="text-xs space-y-0.5 border-b border-zinc-200 pb-1.5">
                <div className="flex flex-wrap gap-2 items-baseline">
                  <span className="font-mono font-bold text-blue-700">{t.sid}</span>
                  <span className="font-bold">{t.friendly_name}</span>
                </div>
                <div className="text-zinc-500 truncate" title={t.body}>{t.body}</div>
                <div className="text-emerald-700 font-mono">
                  Expected variables: {Object.keys(t.variables || {}).length > 0
                    ? Object.entries(t.variables).map(([k, v]) => `${k} (sample: ${v})`).join(', ')
                    : 'none'}
                </div>
              </div>
            ))}
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-300">
            <tr>
              <th className="text-left p-2 font-bold uppercase text-xs text-zinc-700">Event Key</th>
              <th className="text-left p-2 font-bold uppercase text-xs text-zinc-700">Content SID (HX…)</th>
              <th className="text-left p-2 font-bold uppercase text-xs text-zinc-700">Variables (in template order)</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {eventKeys.map(ek => {
              const isMapped = !!(mappings[ek]?.content_sid);
              return (
              <tr key={ek} data-row-key={ek} className={`border-b border-zinc-200 ${!isMapped && health?.unmapped?.includes(ek) ? 'bg-red-50/40' : ''}`}>
                <td className="p-2 font-mono text-xs">
                  <div className="flex items-center gap-1.5">
                    {ek}
                    {isMapped ? (
                      <span className="text-emerald-600 text-[10px]" title="Mapped">✓</span>
                    ) : health?.unmapped?.includes(ek) ? (
                      <span className="text-red-600 text-[10px]" title="Required — not mapped">✗</span>
                    ) : null}
                  </div>
                </td>
                <td className="p-2">
                  <Input data-testid={`csid-${ek}`} className="h-9 font-mono text-xs" placeholder="HX…"
                    value={mappings[ek]?.content_sid || ''} onChange={(e) => setField(ek, 'content_sid', e.target.value)} />
                </td>
                <td className="p-2">
                  <Input data-testid={`cvars-${ek}`} className="h-9 font-mono text-xs" placeholder="customer_name, jo_number"
                    value={mappings[ek]?.variables || ''} onChange={(e) => setField(ek, 'variables', e.target.value)} />
                </td>
                <td className="p-2 text-center">
                  <div className="flex gap-1 justify-end">
                    <Button data-testid={`cautofill-${ek}`} size="sm" variant="outline" onClick={() => autofill(ek)} title="Read variables from Twilio and build the mapping automatically">Auto-fill</Button>
                    <Button data-testid={`csave-${ek}`} size="sm" onClick={() => save(ek)} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
                    {isMapped && (
                      <Button data-testid={`ctest-${ek}`} size="sm" variant="outline"
                              onClick={() => testSend(ek)}
                              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                              title="Send a REAL WhatsApp test using this mapping (with dummy data). Shows the exact ContentVariables JSON posted to Twilio.">
                        Test Send
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export default NotificationsPage;
export { NotificationsPage };
